import { Translation } from '.';
import {
  AddOnType,
  Price,
  TimeInAdvance,
} from '../documents/restaurants/addOn';
import { CustomElement } from './customElements';

type Amount = {
  value: string;
  currency: string;
};

export enum OccassionType {
  DEFAULT = 0,
  FOOD = 1, // Essen, Beides, Sushi, Abendessen,
  DRINKS_ONLY = 2, // Nur Getränke, Cocktails, zum trinken, ...
  TICKET = 3,
  BREAKFAST = 4,
  LUNCH = 5,
  BRUNCH = 6,
  DINNER = 7,
  LIVE_MUSIC = 8,
  LOUNGE = 9,
  COFFEE = 10,
}

export enum OccassionMode {
  RESERVATION = 'reservation', // default
  TICKET = 'ticket',
  EXPERIENCE = 'experience',
  COURSE = 'course',
  MINIMUM_CONSUMPTION = 'minimum_consumption',
  //   RESERVATION_FEE = 'reservation_fee',
}

export type Policy = {
  mode: 'refund' | 'voucher' | 'charge';
  title: string;
  description: string;
  translations: Translation<{
    title?: string;
    description?: string;
  }>;
  link?: string;
} & (
  | { amount: Amount; percentage?: undefined | null }
  | { percentage: number; amount?: undefined | null }
) &
  (
    | {
        type: 'cancellation';
        hoursBefore: number;
      }
    | {
        type: 'no_show';
      }
    | {
        type: 'edit';
        hoursBefore: number;
        fields: string[];
      }
  );

export type SiaOccassionSettings = unknown;

export type Fee = unknown;

export type Occassion = {
  id: string;
  title: string;
  image?: string | null;

  standard?: boolean;

  translations?: Translation<{
    title?: string;
    description?: string;
  }>;

  hideOnAlternateTemplate?: boolean;

  extraType?: string;

  // sia
  type?: OccassionType;
  synonyms?: string[];
  hiddenForSia?: boolean;

  // rwg
  onlyWithTicket?: boolean;

  // mozrest
  includeEventTimesInMozrestFeed?: boolean;

  // frontend
  hidden?: boolean;
  additionalConfirmation?: {
    active: boolean;
    translations: Translation<{ title: string; description: string }>;
  };
  noAvailableTimeMessage?: Record<string, string>;

  // extra
  nextDate?: string;
  minGuests?: number;
  maxGuests?: number;
  minDaysInAdvance?: number;
  minDaysInAdvanceMessage?: Record<string, string>;
};

export type EmailTemplate = {
  active: boolean;
  elements: Translation<{
    title: string;
    emailTitle: string;
    description: string;
    disclaimer: string;
  }>;
  hero: string;
};

export type SMSTemplate = {
  active: boolean;
  message?: Record<string, string>;
  link?: string; // gnaut.me/r/{{id}}
  ts?: number;
};

export type OccassionFallback =
  | {
      type: 'message';
      message: Record<string, string>;
    }
  | {
      type: 'partnerRestaurants';
      partners: { id: string; title: string }[];
    }
  | {
      type: 'occassion';
      occassionId: string;
    };

export type RuleType = 'arrival_time' | 'max_at_same_time' | 'max_per_day';
export type RuleSubject = 'group' | 'guest';

export type Rule = {
  type: RuleType;
  value: number;
  subject: RuleSubject;
  allReservations?: boolean;
};

export type RuleOperator = 'AND' | 'OR';

export interface RuleSet {
  operator: RuleOperator;
  conditions: (Rule | RuleSet)[];
}

export type Rules = {
  rules: RuleSet[];
  disabledAt?: number[];
  minGuests?: number;
  maxGuests?: number;
  unmatchableWithAddOns?: string[];
  otherOptions?: {
    forbidden?: string[];
    required?: string[];
  };
};

/** @deprecated */
export type DependentOn =
  | {
      type: 'option';
      listId: string;
      optionId: string;
    }
  | {
      type: 'inventory';
      inventoryId: string;
      count?: number | string; // "{{guestNumber}}" | 1 | etc
    }
  | {
      type: 'combinations';
      combinations: string[]; // ["101, 102, 103", "104"];
    }
  | {
      type: 'attribute';
      attribute: string;
    }
  | {
      type: 'addOn';
      id: string;
    };

export type OptionListBase = {
  id: string;
  version: number;
  active: boolean;

  optional?: boolean;
  positon?: number;
  hidden?: boolean;

  title: string;
  description: string;

  translations: Translation<{
    title: string;
    description: string;
  }>[];

  selection: 'dietary' | 'seating' | 'other';

  type: AddOnType;

  minTimeInAdvance?: TimeInAdvance;

  afterVoucher?: boolean;

  rules?: Rules;

  updates?: {
    reservationLength?: number;
  };

  // per: 'guest' | 'reservation' | 'multiple';

  // min?: number | null; // only for per: 'multiple' // null === guestNumber
  // max?: number | null; // only for per: 'multiple' // null === guestNumber
  // standard?: number | null; // only for per: 'multiple' // null === guestNumber

  // options: {
  //   id: string;
  //   internalName: string;
  //   title: string;
  //   description?: string;
  //   rules?: {
  //     dependentOn?: DependentOn[];
  //     minGuests?: number;
  //     maxGuests?: number;
  //   };
  //   standard?: boolean;
  //   amount: Amount;
  //   vatRate?: number;
  //   unmatchableWithAddOns?: string[];
  // }[];
};

export type OptionListSimple = {
  type: AddOnType.SIMPLE;
  data: {
    rules?: Rules;
    price: Price;
  };
};

export type OptionListSelect = {
  type: AddOnType.SELECT;
  data: {
    choices: {
      id: number;
      active: boolean;

      title: string;
      description: string;

      translations: Translation<{
        title: string;
        description: string;
      }>[];

      rules?: Rules;

      price: Price;
    }[];
  };
  defaultChoice: number | null;
};

export type OptionListMultipleSelect = {
  type: AddOnType.MULTIPLE_SELECT;
  data: {
    choices: {
      id: number;
      active: boolean;

      title: string;
      description: string;

      translations: Translation<{
        title: string;
        description: string;
      }>[];

      rules?: Rules;

      price: Price;
    }[];
    defaultChoice: number;

    minChoices: number | null;
    maxChoices: number | null;
    defaultChoices: number | null;
  };
};

export type OptionListTableSelect = {
  type: AddOnType.TABLE_SELECT;
  data: {
    where?: {
      type: 'space' | 'table' | 'attribute' | 'occassion';
      ids: string[];
      all?: boolean;
      excludeCombinations?: boolean;
    }[];
    price: Price;
    rules?: Rules;
  };
};

export type OptionList = OptionListBase &
  (
    | OptionListSimple
    | OptionListSelect
    | OptionListMultipleSelect
    | OptionListTableSelect
  );

export interface AdvancedOccassion extends Occassion {
  active?: boolean; // defaults to true

  description?: string;
  mode?: OccassionMode; // defaults to reservation

  minInAdvance?: TimeInAdvance;

  policies?: Policy[]; // @TODO;

  hiddenForRwg?: boolean; // @TODO;

  sia?: SiaOccassionSettings;

  customFields?: CustomElement[]; // @TODO;

  disableFloorPlan?: boolean; // @TODO;

  emails?: {
    confirmation?: EmailTemplate;
    reminder?: EmailTemplate;
    cancelation?: EmailTemplate;
    followUp?: EmailTemplate;
    voucher?: EmailTemplate;
  };

  sms?: {
    confirmation?: SMSTemplate | null;
    reminder?: SMSTemplate | null;
    cancelation?: SMSTemplate | null;
    followUp?: SMSTemplate | null;
  };

  // keepPaymentInformationOnHold?: unknown; // @TODO;

  // minConsumption?: unknown; // @TODO;

  // fee?: Fee; // @TODO;

  voucherId?: string | null;

  pendingTill?: number; // @TODO;

  repeating?: boolean; // cloud function // @TODO;
  inFuture?: boolean; // cloud function // @TODO;

  aliasOf?: string;

  disclaimer?: Translation<string>; // @TODO;

  // course?: unknown;

  // dependentOn?: DependentOn[];

  optionLists?: OptionList[];

  fallbacks?: {
    noShiftOnDay?: OccassionFallback;
    noAvailableTime?: OccassionFallback;
    tooCloseToShift?: OccassionFallback;
    noReservationAtSameDay?: OccassionFallback;
    tooManyGuests?: OccassionFallback;
    tooFewGuests?: OccassionFallback;
  };

  addons?: string[];
  deactiveAddons?: string[];

  // fee
  guestPaysFee?: {
    percentage: number;
    max?: number;
  }; // @TODO;

  noTablesNeeded?: boolean; // for course always true

  pendingAfterBooking?: boolean; // @TODO

  // voucher policies

  // shipping

  // payment
  minAmountToBePaidInAdvance?:
    | {
        percentage: number;
      }
    | {
        amount: Amount;
      }; // @TODO

  // subscriptionId?: string; // @TODO
}

export type OccassionCalendarSheet = {
  dates: {
    [date: string]:
      | null
      | (
          | {
              id: string;
              type: 'open';
              title?: string;
              startingAt?: Amount;
              minGuests?: number;
              maxGuests?: number;
              start: number;
              close: number;
            }
          | {
              type: 'closed';
              fallback?: OccassionFallback;
              title?: string;
            }
        )[];
  };
};

/**
 *
 * - Occassion Write
 * - Reservation Write
 * - ShiftsV02 Write
 * - ReservationDates Write
 *
 */
